import cx from 'classnames';
import { isEmpty } from 'lodash';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';

import { Button, QrCode, QrCodeWrapper } from 'components';
import { CLARITY_MASK_PROPS } from 'constants/clarity.constants';
import { COPY_ICON_PATH, IMAGES_PATH } from 'constants/images.constants';
import { useToast } from 'context';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PaymentField } from 'types';
import { orderRequisitesUtils, formatUtils, clipboardUtils } from 'utils';

export const PaymentFields: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });

  const { showCopiedToast, showCopyFailedToast } = useToast();

  const {
    paymentTypeFlags: { nspkUrl, qrCodeUrl, isSberPay },
    requisitesCardInfo,
    isAccountNumberWithQr,
    shouldShowQrCode,
    requisitesPhone,
    requisitesPaymentLink,
    getFirstRequisitesTradeMethodField,
    formattedPaymentFields,
    requisites,
    tradeMethodSelection: { selectedTradeMethod },
  } = useOrderContext();

  // SberQR Vietnam: prefer the amount-filled payment string, fall back to the raw one
  const paymentStringQrData = useMemo(
    () => requisites?.paymentStringVietnamSberQR || requisites?.paymentString,
    [requisites?.paymentStringVietnamSberQR, requisites?.paymentString],
  );

  const isCopyAvailable = useMemo(
    () => !!navigator?.clipboard?.writeText && !isAccountNumberWithQr,
    [isAccountNumberWithQr],
  );

  const copyCardInfo = useCallback(() => {
    const requisiteDataToCopy =
      requisitesCardInfo ||
      requisitesPhone ||
      requisitesPaymentLink ||
      nspkUrl ||
      getFirstRequisitesTradeMethodField();
    if (!requisiteDataToCopy) {
      return;
    }
    clipboardUtils.copyToClipboardWithFeedback(
      requisiteDataToCopy,
      showCopiedToast,
      showCopyFailedToast,
    );
  }, [
    requisitesCardInfo,
    getFirstRequisitesTradeMethodField,
    requisitesPhone,
    requisitesPaymentLink,
    nspkUrl,
    showCopiedToast,
    showCopyFailedToast,
  ]);

  const renderField = useCallback((field: PaymentField) => {
    let valueNode: React.ReactNode = field.value;
    if (field.name === 'paymentLink') {
      valueNode = (
        <a
          href={field.value}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-break-all tw-text-blue-600 tw-underline hover:tw-text-blue-800 tw-font-semibold tw-transition-colors"
        >
          {orderRequisitesUtils.formatPaymentLink(field.value)}
        </a>
      );
    }

    if (!field.label && !field.value) {
      return null;
    }

    return (
      <div key={field.name} className="tw-mr-3">
        <span className="tw-font-normal">{`${field.label}: `}</span>
        <span {...CLARITY_MASK_PROPS}>{valueNode}</span>
      </div>
    );
  }, []);

  const renderSberPayFields = useCallback(
    () => (
      <Button
        onClick={() =>
          window.open(
            formatUtils.formatApiUrl(requisites?.sberPayUrl),
            '_blank',
            'noopener noreferrer',
          )
        }
        color="success"
        className="tw-flex tw-justify-center tw-items-center tw-mb-5 tw-py-6 tw-text-xl md:tw-text-3xl !tw-text-primary"
      >
        <span className="tw-mb-1">{t('pay')}</span>
        <img
          src={`${IMAGES_PATH}sberpay-logo.svg`}
          className="tw-h-full tw-ml-2"
          alt="sberpay-logo"
        />
      </Button>
    ),
    [requisites?.sberPayUrl, t],
  );

  if (!selectedTradeMethod) {
    return null;
  }

  if (isSberPay) {
    return renderSberPayFields();
  }

  return (
    <div
      className={cx(
        'tw-flex tw-items-center tw-w-full tw-h-auto tw-mb-4 tw-px-3 tw-py-2 tw-rounded-xl tw-bg-layout-accent tw-border-2 tw-border-layout-accent md:tw-px-7 md:tw-py-4 md:tw-rounded-2xl',
      )}
    >
      <div className="tw-flex tw-flex-col tw-text-md tw-font-bold tw-text-primary md:tw-text-xl">
        {formattedPaymentFields.map(renderField)}
      </div>
      <div
        className={cx(
          'tw-flex tw-flex-col tw-justify-center tw-items-center tw-self-start',
          { 'tw-w-full': isEmpty(formattedPaymentFields) },
        )}
      >
        {!isEmpty(formattedPaymentFields) && isCopyAvailable && (
          <Button
            style={{ top: '-1px' }}
            className={cx(
              'tw-flex tw-justify-center tw-items-center tw-self-start !tw-w-10 !tw-text-sm !tw-text-copy !tw-rounded-lg md:!tw-w-40 !tw-h-8 tw-mb-4 -tw-t-1',
            )}
            color="tertiary"
            onClick={copyCardInfo}
          >
            <SVG
              src={COPY_ICON_PATH}
              title="copy"
              className="tw-w-3.5 tw-h-3.5 md:tw-mr-3"
            />
            <div className="tw-hidden md:tw-block">{t('buttons.copy')}</div>
          </Button>
        )}
        {paymentStringQrData ? (
          <QrCode data={paymentStringQrData} className="tw-w-full md:tw-p-1" />
        ) : (
          shouldShowQrCode && (
            <QrCodeWrapper
              qrCodeUrl={qrCodeUrl}
              paymentLink={requisites?.paymentLink}
              bankName={requisites?.bankName}
            />
          )
        )}
      </div>
    </div>
  );
};
